// web/src/pages/Compte.jsx
import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { apiFetch } from "../utils/api";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { resultPathFromBanId } from "../utils/resultUrl";

const API_PATHS = {
  changeName: "/api/auth/change-name",
  changeEmail: "/api/auth/change-email",
  changePass: "/api/auth/change-password",
  requestVerifyEmail: "/api/auth/request-email-verification",
  favorites: "/api/me/favorites",
};

const API_DELETE = {
  delete: "/api/auth/delete-account",
  restore: "/api/auth/restore-account",
};

const DELETE_LS_KEY = "delete_until_iso";

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10001,
        background: "rgba(0,0,0,.45)",
        display: "grid",
        placeItems: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: 18,
          maxWidth: 480,
          width: "calc(100% - 32px)",
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 18px 48px rgba(0,0,0,0.20)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: "4px 0 10px", fontSize: 18 }}>{title}</h3>
          <button
            onClick={onClose}
            className="btn btn-light"
            title="Fermer"
            style={{ padding: "6px 10px" }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function HistoryList() {
  const [hist, setHist] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch("/api/me/history");
        if (r.ok) {
          const arr = await r.json();
          setHist((Array.isArray(arr) ? arr : []).slice(0, 10));
        }
      } catch {}
    })();
  }, []);

  const open = (ban_id) => {
    if (!ban_id) return;
    window.location.href = resultPathFromBanId(ban_id);
  };

  if (hist.length === 0) {
    return <div className="muted">Aucune adresse récemment visitée.</div>;
  }

  return (
    <ul className="recent-list">
      {hist.map((r, i) => {
        const label =
          r.label ||
          (r.ban_id?.split(" ").slice(0, -2).join(" ")) ||
          r.ban_id;

        return (
          <li key={`${r.ban_id}-${i}`} className="glass-chip recent-entry">
            <button
              onClick={() => open(r.ban_id)}
              className="recent-link"
              title="Ouvrir le rapport"
            >
              <span aria-hidden="true">📍</span>
              <span style={{ overflowWrap: "anywhere" }}>{label}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export default function Compte() {
  const { user, refresh, logout } = useAuth();

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const [favorites, setFavorites] = useState([]);

  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editEmailOpen, setEditEmailOpen] = useState(false);
  const [editPassOpen, setEditPassOpen] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);

  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");

  const [deleteNotice, setDeleteNotice] = useState({ pending: false, endStr: "" });

  const fetchFavorites = async () => {
    try {
      const r = await apiFetch(API_PATHS.favorites);
      if (r.ok) setFavorites(await r.json());
    } catch {}
  };

  useEffect(() => {
    (async () => {
      await fetchFavorites();
    })();
  }, []);

  useEffect(() => {
    setName(user?.name || "");
    setEmail(user?.email || "");
  }, [user]);

  useEffect(() => {
    const iso = localStorage.getItem(DELETE_LS_KEY);
    if (iso) {
      const dt = new Date(iso);
      if (!Number.isNaN(dt.valueOf()) && dt.getTime() > Date.now()) {
        setDeleteNotice({ pending: true, endStr: dt.toLocaleDateString("fr-FR") });
      } else {
        localStorage.removeItem(DELETE_LS_KEY);
        setDeleteNotice({ pending: false, endStr: "" });
      }
    } else {
      setDeleteNotice({ pending: false, endStr: "" });
    }
  }, [user]);

  const call = async (path, body) => {
    const r = await apiFetch(path, { method: "POST", body: JSON.stringify(body || {}) });
    if (!r.ok) throw new Error(await r.text());
    await refresh();
  };

  const onChangeName = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return;
    try {
      setBusy(true);
      setMsg("");
      await call(API_PATHS.changeName, { name: name.trim() });
      setMsg("Nom d'utilisateur mis à jour.");
      setEditNameOpen(false);
    } catch {
      setMsg("Échec mise à jour du nom d'utilisateur.");
    } finally {
      setBusy(false);
    }
  };

  const onChangeEmail = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    try {
      setBusy(true);
      setMsg("");
      await call(API_PATHS.changeEmail, { email: email.trim() });
      setMsg("Email mis à jour. Vérifiez votre boîte mail pour confirmer.");
      setEditEmailOpen(false);
    } catch {
      setMsg("Échec mise à jour de l’email (peut-être déjà utilisé ?).");
    } finally {
      setBusy(false);
    }
  };

  const onChangePass = async (e) => {
    e?.preventDefault?.();
    const form = new FormData(e.currentTarget);
    const cur = String(form.get("cur") || "");
    do {
      const np = String(form.get("np") || "");
      const np2 = String(form.get("np2") || "");
      if (!cur || !np || !np2) {
        setMsg("Tous les champs mot de passe sont requis.");
        break;
      }
      if (np !== np2) {
        setMsg("Les 2 nouveaux mots de passe ne correspondent pas.");
        break;
      }
      if (np.length < 8) {
        setMsg("Mot de passe trop court (min 8).");
        break;
      }
      try {
        setBusy(true);
        setMsg("");
        await call(API_PATHS.changePass, { current_password: cur, new_password: np });
        setMsg("Mot de passe mis à jour.");
        setEditPassOpen(false);
      } catch {
        setMsg("Échec mise à jour du mot de passe.");
      } finally {
        setBusy(false);
      }
      return;
    } while (false);
  };

  const requestVerify = async () => {
    try {
      await apiFetch(API_PATHS.requestVerifyEmail, { method: "POST" });
      alert("Email de vérification envoyé. Surveillez aussi dans vos spams.");
    } catch {}
  };

  const emailVerified = !!user?.email_verified;

  const onDeleteAccount = () => setDeleteOpen(true);

  const performDelete = async () => {
    try {
      setBusy(true);
      setMsg("");
      const r = await apiFetch(API_DELETE.delete, { method: "POST" });
      let pendingUntil = null;
      if (r.ok) {
        try {
          const data = await r.json();
          pendingUntil = data?.pending_until || null;
        } catch {}
      } else {
        throw new Error(await r.text());
      }

      if (pendingUntil) {
        localStorage.setItem(DELETE_LS_KEY, pendingUntil);
        const dt = new Date(pendingUntil);
        setDeleteNotice({
          pending: true,
          endStr: !Number.isNaN(dt.valueOf()) ? dt.toLocaleDateString("fr-FR") : "",
        });
        setMsg("Suppression de compte programmée. Vous pouvez annuler avant la date indiquée.");
        setDeleteOpen(false);
        await refresh();
      } else {
        setDeleteOpen(false);
        await logout();
        window.location.href = "/";
      }
    } catch (e) {
      console.error(e);
      alert("Suppression impossible.");
    } finally {
      setBusy(false);
    }
  };

  const restoreDeletion = async () => {
    try {
      setBusy(true);
      setMsg("");
      const r = await apiFetch(API_DELETE.restore, { method: "POST" });
      if (!r.ok) throw new Error(await r.text());
      localStorage.removeItem(DELETE_LS_KEY);
      setDeleteNotice({ pending: false, endStr: "" });
      setMsg("Suppression annulée.");
      await refresh();
    } catch (e) {
      console.error(e);
      alert("Annulation impossible.");
    } finally {
      setBusy(false);
    }
  };

  const openFav = (ban_id) => {
    if (!ban_id) return;
    window.location.href = resultPathFromBanId(ban_id);
  };

  const removeFav = async (ban_id) => {
    try {
      const r = await apiFetch(API_PATHS.favorites, {
        method: "DELETE",
        body: JSON.stringify({ ban_id }),
      });
      if (!r.ok) throw new Error(await r.text());
      setFavorites((prev) => prev.filter((x) => x.ban_id !== ban_id));
    } catch (e) {
      console.error(e);
      alert("Suppression impossible.");
    }
  };

  return (
    <div className="results-page">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Mon compte – État du Lieu</title>
      </Helmet>

      <style>{`
        :root{
          --bg1:#3b82f6; --bg2:#1e40af;
          --surface:rgba(255,255,255,0.95);
          --ink:#0f172a; --muted:#64748b;
          --blue1:#3b82f6; --blue2:#1d4ed8;
          --green1:#22c55e; --green2:#16a34a;
          --auth-reserve: 0px;
        }

        html, body {
          margin: 0;
          padding: 0;
          color-scheme: light;
          background-color: transparent;
        }

        [data-header-auth]{ display:none !important; }

        .results-page{
          min-height:100vh;
          background: linear-gradient(135deg,var(--bg1) 0%, var(--bg2) 100%);
          font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen,Ubuntu,Cantarell,sans-serif;
          color:var(--ink);
          position: relative;
        }
        .wrap{
          max-width: 980px;
          margin: 0 auto;
          padding: 28px 16px 64px;
        }
        @media (max-width: 640px){
          :root{ --auth-reserve: max(76px, calc(env(safe-area-inset-top, 0px) + 64px)); }
          .wrap{ padding-top: calc(28px + var(--auth-reserve)); }
        }
        .glass-chip{
          background: var(--surface); backdrop-filter: blur(18px);
          border: 1px solid rgba(255,255,255,0.22);
          border-radius: 14px; padding: 8px 10px;
          box-shadow: 0 12px 28px rgba(0,0,0,0.12);
          display: inline-flex; align-items:center; gap:10px;
        }
        .back-chip a{
          text-decoration:none;
          color:var(--ink);
          font-weight:600;
        }
        @media (max-width: 400px){
          .back-chip a{ font-size: 12px; }
        }
        .card{
          background: var(--surface); backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.22); border-radius: 18px;
          padding: 16px; box-shadow: 0 16px 36px rgba(0,0,0,0.12); margin-bottom: 16px;
        }
        .row{ display:flex; gap:12px; flex-wrap: wrap; align-items:center; }
        .headerauth-inline{ display:none !important; }
        .btn {
          display:inline-flex;
          align-items:center;
          gap:10px;
          padding:10px 16px;
          border-radius:10px;
          font-weight:800;
          cursor:pointer;
          border:0;
        }
        .btn-light { background:#f3f4f6; color:#0f172a; border:1px solid #e5e7eb; }
        .btn-primary { background:linear-gradient(135deg,var(--blue1),var(--blue2)); color:#fff; }
        .btn-success { background:linear-gradient(135deg,var(--green1),var(--green2)); color:#fff; }
        .btn-danger { background:#fee2e2; color:#991b1b; border:1px solid #fecaca; }
        .btn:disabled{ opacity:.7; cursor:not-allowed; }
        .muted{ color:#64748b; }
        .kv{ display:grid; grid-template-columns: minmax(140px, 200px) 1fr; gap:10px; align-items:center; width: 100%; }
        @media (max-width: 640px){ .kv{ grid-template-columns: 1fr; } }
        .monos{
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        input[type="text"], input[type="email"], input[type="password"]{
          width: 100%; box-sizing: border-box; padding: 10px; margin: 6px 0 12px; border-radius: 10px; border: 1px solid #e5e7eb; max-width: 100%;
        }
        .fav-list,
        .recent-list{
          list-style:none;
          margin:0;
          padding:0;
          display:grid;
          gap:8px;
        }
        .fav-entry,
        .recent-entry{
          display:flex;
          justify-content:space-between;
          align-items:center;
          gap:12px;
          flex-wrap: wrap;
        }
        .fav-link,
        .recent-link{
          all:unset;
          cursor:pointer;
          display:inline-flex;
          align-items:center;
          gap:10px;
          font-weight:800;
        }
      `}</style>

      <div className="wrap">
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
          <div className="glass-chip back-chip">
            <Link to="/" aria-label="Revenir à la recherche">
              ← Retour
            </Link>
          </div>
          {!emailVerified && user && (
            <div className="glass-chip">
              <div>Email non vérifié ❌</div>
              <div style={{ fontSize: "0.8em", opacity: 0.8, marginTop: 2 }}>
                Surveillez aussi dans vos spams.
              </div>
            </div>
          )}
        </div>

        {deleteNotice.pending && (
          <div className="glass-chip" style={{ marginBottom: 12 }}>
            ⚠️ Suppression de compte programmée. Compte supprimé après le{" "}
            <strong>{deleteNotice.endStr || "—"}</strong>.
            <div className="row" style={{ marginTop: 8 }}>
              <button className="btn btn-light" onClick={restoreDeletion} disabled={busy}>
                Annuler la suppression
              </button>
            </div>
          </div>
        )}

        <div className="card">
          <h2 style={{ marginTop: 0 }}>Mon compte</h2>
          <div className="muted" style={{ marginBottom: 14, overflowWrap: "anywhere" }}>
            Bonjour, <strong>{user?.name || user?.email || "invité"}</strong>
          </div>

          <div className="card" style={{ paddingTop: 10 }}>
            <div className="kv">
              <div className="muted">Nom d'utilisateur</div>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <div style={{ overflowWrap: "anywhere" }}>
                  <strong>{user?.name || "—"}</strong>
                </div>
                <button
                  className="btn btn-light"
                  onClick={() => setEditNameOpen(true)}
                  disabled={!user || busy}
                >
                  Modifier
                </button>
              </div>

              <div className="muted">Email</div>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <div className="monos">{user?.email || "—"}</div>
                <div className="row" style={{ gap: 8 }}>
                  {user && !emailVerified && (
                    <button
                      className="btn btn-light"
                      onClick={requestVerify}
                      disabled={busy}
                    >
                      Renvoyer l'e-mail de vérification
                    </button>
                  )}
                  <button
                    className="btn btn-light"
                    onClick={() => setEditEmailOpen(true)}
                    disabled={!user || busy}
                  >
                    Modifier
                  </button>
                </div>
              </div>

              <div className="muted">Mot de passe</div>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <div className="monos" aria-hidden="true">••••••••</div>
                <button
                  className="btn btn-light"
                  onClick={() => setEditPassOpen(true)}
                  disabled={!user || busy}
                >
                  Modifier
                </button>
              </div>
            </div>
          </div>

          {msg && <div className="glass-chip" style={{ marginTop: 10 }}>{msg}</div>}

          <div className="row" style={{ marginTop: 14 }}>
            <button
              className="btn btn-light"
              onClick={async () => { await fetchFavorites(); await refresh(); }}
              disabled={busy}
            >
              ↻ Rafraîchir
            </button>
            {user && (
              <>
                <button className="btn btn-danger" onClick={logout} disabled={busy}>
                  Se déconnecter
                </button>
                <button
                  className="btn btn-danger"
                  onClick={onDeleteAccount}
                  title="Supprimer définitivement mon compte"
                  disabled={busy}
                >
                  Supprimer mon compte
                </button>
              </>
            )}
          </div>
        </div>

        <div className="card">
          <h2 style={{ marginTop: 0 }}>Historique (10 dernières adresses)</h2>
          <HistoryList />
        </div>

        <div className="card">
          <h2 style={{ marginTop: 0 }}>Mes favoris</h2>
          {favorites.length === 0 ? (
            <div className="muted">Aucun favori pour l’instant.</div>
          ) : (
            <ul className="fav-list">
              {favorites.map((f) => {
                const label =
                  f.label ||
                  (f.ban_id?.split(" ").slice(0, -2).join(" ")) ||
                  f.ban_id;
                return (
                  <li key={f.ban_id} className="glass-chip fav-entry">
                    <button
                      onClick={() => openFav(f.ban_id)}
                      title="Ouvrir le rapport"
                      className="fav-link"
                    >
                      <span aria-hidden="true">📍</span>
                      <span style={{ overflowWrap: "anywhere" }}>{label}</span>
                    </button>
                    <button
                      className="btn btn-light"
                      onClick={() => removeFav(f.ban_id)}
                      title="Retirer des favoris"
                    >
                      Retirer
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <Modal
        open={editNameOpen}
        onClose={() => setEditNameOpen(false)}
        title="Changer le nom d'utilisateur"
      >
        <form onSubmit={onChangeName}>
          <label>Nom d'utilisateur</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              type="button"
              className="btn btn-light"
              onClick={() => setEditNameOpen(false)}
            >
              Annuler
            </button>
            <button className="btn btn-primary" disabled={busy}>
              {busy ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={editEmailOpen}
        onClose={() => setEditEmailOpen(false)}
        title="Changer l’email"
      >
        <form onSubmit={onChangeEmail}>
          <label>Nouvel email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
          />
          <div className="muted" style={{ marginBottom: 10 }}>
            Un e-mail de confirmation sera envoyé.
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              type="button"
              className="btn btn-light"
              onClick={() => setEditEmailOpen(false)}
            >
              Annuler
            </button>
            <button className="btn btn-primary" disabled={busy}>
              Mettre à jour
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={editPassOpen}
        onClose={() => setEditPassOpen(false)}
        title="Changer le mot de passe"
      >
        <form onSubmit={onChangePass}>
          <label>Mot de passe actuel</label>
          <input
            name="cur"
            type="password"
            autoComplete="current-password"
            disabled={busy}
          />
          <label>Nouveau mot de passe</label>
          <input
            name="np"
            type="password"
            autoComplete="new-password"
            disabled={busy}
          />
          <label>Répéter le nouveau mot de passe</label>
          <input
            name="np2"
            type="password"
            autoComplete="new-password"
            disabled={busy}
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              type="button"
              className="btn btn-light"
              onClick={() => setEditPassOpen(false)}
            >
              Annuler
            </button>
            <button className="btn btn-primary" disabled={busy}>
              Mettre à jour
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Supprimer mon compte"
      >
        <p className="muted" style={{ marginTop: 0 }}>
          Cette action est <strong>irréversible</strong>. Vos données de compte,
          favoris et historiques seront supprimées.
        </p>
        <p className="muted">
          Si disponible, nous programmerons une suppression différée de{" "}
          <strong>14 jours</strong> pendant laquelle vous pourrez annuler.
        </p>
        <div
          className="row"
          style={{ justifyContent: "flex-end", marginTop: 10 }}
        >
          <button
            className="btn btn-light"
            onClick={() => setDeleteOpen(false)}
          >
            Annuler
          </button>
          <button
            className="btn btn-danger"
            onClick={performDelete}
            disabled={busy}
          >
            {busy ? "…" : "Supprimer mon compte"}
          </button>
        </div>
      </Modal>
    </div>
  );
}